import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useSubmitEvent } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { CreditCard, CheckCircle, AlertCircle, Send } from "lucide-react";
import { useCategories } from "@/hooks/use-categories";
import { TownSelect } from "@/components/TownSelect";
import { ImageUpload } from "@/components/ImageUpload";
import { useAnalytics } from "@/hooks/use-analytics";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const STORAGE_KEY = "pending_event_form";

export default function SubmitEvent() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { mutateAsync: submitEvent, isPending } = useSubmitEvent();
  const { track } = useAnalytics();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const categories = useCategories("event");
  const [category, setCategory] = useState("");
  const [town, setTown] = useState("Ayr");
  const [venue, setVenue] = useState("");
  const [venueId, setVenueId] = useState<number | null>(null);
  const [myVenues, setMyVenues] = useState<{ id: number; name: string; town: string | null }[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [eventPrice, setEventPrice] = useState("Free");
  const [organiserName, setOrganiserName] = useState(user?.name ?? "");
  const [imageUrl, setImageUrl] = useState("");
  const [listingFee, setListingFee] = useState<number | null>(null);
  const [feeLoading, setFeeLoading] = useState(true);
  const [paymentHandled, setPaymentHandled] = useState(false);

  useEffect(() => {
    fetch("/api/stripe/price?type=event", { headers: authHeaders() })
      .then(r => r.json())
      .then((d: { price: number }) => setListingFee(d.price ?? 0))
      .catch(() => setListingFee(0))
      .finally(() => setFeeLoading(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentSuccess = params.get("payment_success");
    const sessionId = params.get("session_id");
    if (paymentSuccess === "1" && sessionId) {
      const savedForm = sessionStorage.getItem(STORAGE_KEY);
      if (savedForm) {
        const formData = JSON.parse(savedForm) as typeof formFields;
        setName(formData.name); setDescription(formData.description);
        setCategory(formData.category); setTown(formData.town);
        setVenue(formData.venue); setVenueId(formData.venueId ?? null); setEventDate(formData.eventDate);
        setEventTime(formData.eventTime); setEventPrice(formData.eventPrice);
        setOrganiserName(formData.organiserName);
        if (formData.imageUrl) setImageUrl(formData.imageUrl);
        sessionStorage.removeItem(STORAGE_KEY);
        setPaymentHandled(true);
        window.history.replaceState({}, "", window.location.pathname);
        void (async () => {
          try {
            await submitEvent({ data: { ...formData, stripeSessionId: sessionId } as never });
            toast({ title: "Event submitted!", description: "Your event is pending review. Payment confirmed ✓" });
            setLocation("/events");
          } catch {
            toast({ title: "Submission failed", description: "Payment was taken but submission failed. Please contact us.", variant: "destructive", action: <ToastAction altText="Email us" onClick={() => { window.location.href = "mailto:hello@ayrshirehub.co.uk"; }}>Contact us</ToastAction> });
          }
        })();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!category && categories.length > 0) setCategory(categories[0]!);
  }, [categories]);

  useEffect(() => {
    fetch("/api/my-venues", { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { items: [] })
      .then((d: { items: { id: number; name: string; town: string | null }[] }) => setMyVenues(d.items ?? []))
      .catch(() => setMyVenues([]))
      .finally(() => setVenuesLoading(false));
  }, []);

  const formFields = { name, description, category, town, venue, venueId, eventDate, eventTime, eventPrice, organiserName, imageUrl };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">🎉</div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Sign in to submit an event</h1>
          <p className="text-slate-500 text-sm mb-6">Create a free account to add events to the Ayrshire Hub community calendar.</p>
          <div className="flex gap-3">
            <Link href="/login" className="flex-1"><Button variant="outline" className="w-full">Login</Button></Link>
            <Link href="/signup" className="flex-1"><Button className="w-full bg-primary hover:bg-primary/90">Create Account</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  if (user.emailVerified === false) {
    return <EmailVerificationGate email={user.email} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !description || !eventDate) {
      toast({ title: "Missing fields", description: "Please fill in the event name, description, and date.", variant: "destructive" });
      return;
    }


    if (listingFee && listingFee > 0 && !paymentHandled) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formFields));
      try {
        const res = await fetch("/api/stripe/checkout", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            amount: listingFee / 100,
            description: "Event Listing Fee — Ayrshire Hub",
            successUrl: `${window.location.origin}${window.location.pathname}?payment_success=1&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: window.location.href,
            metadata: { type: "event" },
          }),
        });
        const data = await res.json() as { url?: string; error?: string };
        if (data.url) { window.location.href = data.url; return; }
        throw new Error(data.error ?? "Payment setup failed");
      } catch (err: unknown) {
        sessionStorage.removeItem(STORAGE_KEY);
        toast({ title: "Payment error", description: err instanceof Error ? err.message : "Could not start payment. Please try again.", variant: "destructive", action: <ToastAction altText="Try again" onClick={() => void handleSubmit({ preventDefault: () => {} } as React.FormEvent)}>Try again</ToastAction> });
        return;
      }
    }

    try {
      await submitEvent({ data: { name, description, category, town, venue, venueId: venueId ?? undefined, eventDate, eventTime, price: eventPrice, organiserName, imageUrl: imageUrl || undefined } as never });
      track("event_submitted", { screen: "submit-event", metadata: { category, town, isPaid: false } });
      toast({ title: "Event submitted!", description: "Your event is pending review and will appear once approved." });
      setLocation("/events");
    } catch {
      toast({ title: "Error", description: "Could not submit your event. Please try again.", variant: "destructive", action: <ToastAction altText="Try again" onClick={() => void handleSubmit({ preventDefault: () => {} } as React.FormEvent)}>Try again</ToastAction> });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <Link href="/events" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-primary mb-6 transition-colors">
          ← Back to Events
        </Link>

        <div className="bg-primary rounded-2xl p-5 mb-4 flex items-center gap-4">
          <span className="text-3xl">🎉</span>
          <div>
            <h1 className="text-xl font-bold text-white">Submit an Event</h1>
            <p className="text-white/70 text-sm">Logged in as {user.name}</p>
          </div>
        </div>

        <div className="bg-primary/5 border border-primary/15 rounded-xl p-3 mb-4 flex items-start gap-2 text-sm text-primary">
          ℹ️ Events are reviewed before going live. We aim to approve within 24 hours.
        </div>

        {!feeLoading && listingFee !== null && listingFee > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-4 mb-4 flex items-center gap-3 text-sm">
            <CreditCard size={18} className="text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800 dark:text-amber-300">Listing fee: £{(listingFee / 100).toFixed(2)}</p>
              <p className="text-amber-700">You'll be directed to a secure payment page before your event is submitted.</p>
            </div>
          </div>
        )}
        {!feeLoading && (listingFee === 0 || listingFee === null) && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex items-center gap-2 text-sm text-emerald-700">
            <CheckCircle size={16} className="flex-shrink-0" />
            Events are currently free to list!
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Section title="Event Details">
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Event Name *</label>
                <Input required placeholder="e.g. Ayrshire Farmers Market" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Description *</label>
                <Textarea required rows={4} placeholder="Tell people about your event..." value={description} onChange={e => setDescription(e.target.value)} />
              </div>
            </div>
          </Section>

          <Section title="Category">
            <div className="p-3 flex flex-wrap gap-2">
              {categories.map(cat => (
                <button key={cat} type="button" onClick={() => setCategory(cat)}
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${category === cat ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:border-primary hover:text-primary"}`}>
                  {cat}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Date & Time">
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Date * (YYYY-MM-DD)</label>
                <Input required type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Time</label>
                <Input type="time" value={eventTime} onChange={e => setEventTime(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Ticket Price</label>
                <Input placeholder="Free or £10 per person" value={eventPrice} onChange={e => setEventPrice(e.target.value)} />
              </div>
            </div>
          </Section>

          <Section title="Location">
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Venue (optional)</label>
                {venuesLoading ? (
                  <div className="h-9 bg-slate-100 rounded-lg animate-pulse" />
                ) : myVenues.length > 0 ? (
                  <select
                    value={venueId ?? ""}
                    onChange={e => {
                      const v = myVenues.find(x => x.id === Number(e.target.value));
                      if (v) { setVenueId(v.id); setVenue(v.name); }
                    }}
                    className="w-full h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-primary/40"
                  >
                    <option value="" disabled>Select your venue…</option>
                    {myVenues.map(v => (
                      <option key={v.id} value={v.id}>{v.name}{v.town ? ` (${v.town})` : ""}</option>
                    ))}
                  </select>
                ) : (
                  <div className="space-y-2">
                    <Input
                      placeholder="e.g. The Grand Hall, Kilmarnock"
                      value={venue}
                      onChange={e => setVenue(e.target.value)}
                    />
                    <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg px-3 py-2.5">
                      <span className="text-amber-500 text-sm">🏠</span>
                      <div className="flex-1 text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                        <strong>Own a pub or venue?</strong> Register it first so your events are linked to your venue listing.{" "}
                        <a href="/list-your-venue" className="underline font-semibold text-amber-900 dark:text-amber-300 hover:text-amber-700">Register your venue →</a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Town</label>
                <TownSelect value={town} onChange={setTown} />
              </div>
            </div>
          </Section>

          <Section title="Organiser">
            <div className="p-4">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Your Name or Organisation</label>
              <Input placeholder="e.g. Ayrshire Events Ltd" value={organiserName} onChange={e => setOrganiserName(e.target.value)} />
            </div>
          </Section>

          <Section title="Event Image">
            <div className="p-4">
              <p className="text-xs text-slate-400 mb-3">Upload your own photo or choose a free stock image from our gallery.</p>
              <ImageUpload
                value={imageUrl}
                onChange={setImageUrl}
                label="Upload Image"
                stockCategory="event"
              />
            </div>
          </Section>

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 font-bold text-base py-6"
            disabled={isPending || feeLoading}
          >
            {feeLoading ? "Loading…" : isPending ? "Submitting…" : listingFee && listingFee > 0 ? `📤 Submit & Pay £${(listingFee / 100).toFixed(2)}` : "📤 Submit Event"}
          </Button>
          <p className="text-xs text-center text-slate-400 pb-4">
            Events are reviewed before going live. We aim to approve within 24 hours.
          </p>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-slate-700 mb-1.5 px-1">{title}</h3>
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">{children}</div>
    </div>
  );
}

function EmailVerificationGate({ email }: { email: string }) {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { Authorization: `Bearer ${token ?? ""}` },
      });
      toast({ title: "Verification email sent!", description: "Check your inbox and spam/junk folder for the link." });
    } catch {
      toast({ title: "Could not send email", description: "Please try again shortly.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-8 max-w-md w-full text-center">
        <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="h-7 w-7 text-amber-500" />
        </div>
        <h1 className="text-xl font-bold text-slate-800 mb-2">Verify your email first</h1>
        <p className="text-slate-600 text-sm mb-2">
          You need to verify your email address before you can submit events or upload images.
        </p>
        <p className="text-slate-500 text-sm mb-6">
          We sent a verification link to <strong>{email}</strong>.
          Can't find it? <strong>Check your spam or junk folder</strong> — it sometimes lands there.
        </p>
        <Button
          onClick={handleResend}
          disabled={sending}
          className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold mb-3"
        >
          <Send className="h-4 w-4 mr-2" />
          {sending ? "Sending…" : "Resend verification email"}
        </Button>
        <Link href="/events" className="text-sm text-slate-400 hover:text-slate-600 underline">
          Back to Events
        </Link>
      </div>
    </div>
  );
}
